/**
 * Settings Service API
 * React Query hooks for user preferences and notification settings
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { get, put } from '@/lib/api/client'
import { useIsAuthenticated } from '@/lib/auth/useAuth'

// Query keys
export const settingsKeys = {
  all: ['settings'] as const,
  preferences: () => [...settingsKeys.all, 'preferences'] as const,
  preferenceCategory: (category: string) => [...settingsKeys.preferences(), category] as const,
  notifications: () => [...settingsKeys.all, 'notifications'] as const,
}

// Types
export interface UserPreferences {
  language: string
  timezone: string
  dateFormat: string
  numberFormat: string
  theme: 'light' | 'dark' | 'system'
  /** One of the 10 global color themes (see src/lib/themes.ts). Stored in display category. */
  colorTheme: string
  accentColor: string
  fontSize: 'small' | 'medium' | 'large'
  compactMode: boolean
  showAnimations: boolean
  defaultDashboardView: string
  itemsPerPage: number
  autoSave: boolean
  keyboardShortcuts: boolean
  highContrast: boolean
  reduceMotion: boolean
}

export interface NotificationSettings {
  channels: { email: boolean; inApp: boolean; sms: boolean }
  frequency: string
  quiet_hours_enabled: boolean
  quiet_hours_start: string
  quiet_hours_end: string
  mute_all: boolean
  categories: Record<string, Record<string, any>>
}

// Default values (fallback while loading)
export const defaultPreferences: UserPreferences = {
  language: 'fr',
  timezone: 'America/Montreal',
  dateFormat: 'DD/MM/YYYY',
  numberFormat: 'fr-CA',
  theme: 'system',
  colorTheme: 'ocean',
  accentColor: 'blue',
  fontSize: 'medium',
  compactMode: false,
  showAnimations: true,
  defaultDashboardView: 'overview',
  itemsPerPage: 25,
  autoSave: true,
  keyboardShortcuts: true,
  highContrast: false,
  reduceMotion: false,
}

export const defaultNotificationSettings: NotificationSettings = {
  channels: { email: true, inApp: true, sms: false },
  frequency: 'instant',
  quiet_hours_enabled: false,
  quiet_hours_start: '22:00',
  quiet_hours_end: '08:00',
  mute_all: false,
  categories: {},
}

// ── User Preferences Hooks ──────────────────────────────────────────────────

export function useUserPreferences() {
  // Gate on auth-ready: this hook is consumed by the global ColorThemeProvider,
  // so without `enabled` it fires on every (re)mount before keycloak-js has
  // attached the token, producing a transient 401 on /settings/user/preferences
  // on every page. Waiting for isAuthenticated guarantees the bearer is present.
  const isAuthenticated = useIsAuthenticated()
  return useQuery({
    queryKey: settingsKeys.preferences(),
    queryFn: async () => {
      const response = await get<{ preferences: Record<string, Record<string, any>> }>('/settings/user/preferences')
      // Merge display + accessibility categories into a flat Preferences object
      const display = response.preferences?.display || {}
      const accessibility = response.preferences?.accessibility || {}
      return { ...defaultPreferences, ...display, ...accessibility } as UserPreferences
    },
    enabled: isAuthenticated,
    staleTime: 5 * 60 * 1000, // 5 minutes
  })
}

export function useUpdateUserPreferences() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (prefs: Partial<UserPreferences>) => {
      // Split into display and accessibility categories
      const accessibilityKeys = ['keyboardShortcuts', 'highContrast', 'reduceMotion']
      const display: Record<string, any> = {}
      const accessibility: Record<string, any> = {}

      for (const [key, value] of Object.entries(prefs)) {
        if (accessibilityKeys.includes(key)) {
          accessibility[key] = value
        } else {
          display[key] = value
        }
      }

      // Save both categories
      const promises: Promise<any>[] = []
      if (Object.keys(display).length > 0) {
        promises.push(put('/settings/user/preferences/display', { preferences: display }))
      }
      if (Object.keys(accessibility).length > 0) {
        promises.push(put('/settings/user/preferences/accessibility', { preferences: accessibility }))
      }
      await Promise.all(promises)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: settingsKeys.preferences() })
    },
  })
}

// ── Notification Settings Hooks ─────────────────────────────────────────────

export function useNotificationSettings() {
  return useQuery({
    queryKey: settingsKeys.notifications(),
    queryFn: async () => {
      const response = await get<NotificationSettings>('/settings/user/notifications')
      return { ...defaultNotificationSettings, ...response }
    },
    staleTime: 5 * 60 * 1000,
  })
}

export function useUpdateNotificationSettings() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (settings: Partial<NotificationSettings>) => {
      return put<NotificationSettings>('/settings/user/notifications', settings)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: settingsKeys.notifications() })
    },
  })
}
